import { BalanceSheetModel } from "@/models/balanceSheet.model";
import { Equipment, Extractor, Point } from "@/models/equipment.model";
import { Settings } from "@/models/settings.model";
import { DepressionCalculatedValues, ElementaryZone, SiteModel } from "@/models/site.model";

/**
 * calculate debit from abacus
 * @param abacus array of points
 * @param depression depression choose by user
 */
export function calculateDebitFromAbacus(abacus: Point[], depression: number): number {
  const targetDebit = abacus.find((point) => point.x == depression);
  if (targetDebit) {
    return targetDebit.y;
  }
  const closestInferiorNumber = getClosestInferiorValue(abacus, depression);
  const closestSuperiorNumber = abacus[abacus.indexOf(closestInferiorNumber) + 1];
  return getPointByInterpolation(closestInferiorNumber, closestSuperiorNumber, depression);
}

/**
 * get closest x axis infieror value in an array of points
 * @param abacus array of points
 * @param goal reference x point
 */
function getClosestInferiorValue(abacus: Point[], goal: number): Point {
  return abacus.reduce(function (prev, curr) {
    return Math.abs(curr.x - goal) < Math.abs(prev.x - goal) && curr.x < goal ? curr : prev;
  });
}

/**
 * get approximate value between two point considering trajectory is linear
 * @param pointA
 * @param pointB
 */
function getPointByInterpolation(pointA: Point, pointB: Point, goal: number): number {
  return Number((pointA.y + (goal - pointA.x) * ((pointB.y - pointA.y) / (pointB.x - pointA.x))).toFixed(0));
}

/**
 * calculate total sas debit
 */
export function calculateSasTotalZonesDebit(
  depressionCalculatedValues: DepressionCalculatedValues
): number {
  return depressionCalculatedValues.personnelSasDebit + depressionCalculatedValues.wastesSasDebit;
}

/**
 * minimal air debit in a zone
 * @param volume represents the volume of the zone
 * @param renewalRate represents the renewal rate chosen by the user
 * @param totalZoneDebit represents totalZone debit
 */
export function minimalAirZoneDebit(
  renewalRate: number,
  volume: number,
  totalZoneDebit: number
): number {
  return renewalRate * volume - totalZoneDebit;
}

/**
 * calculate necessary number of eacm zone
 * @param minimalAirDebit represents total minimal air debit
 * @param debit debit of eacm from abacus
 */
export function numberZoneEacm(minimalAirDebit: number, debit: number): number {
  const nbr = Math.ceil(minimalAirDebit / debit);
  return nbr > 0 ? nbr : 0;
}

/**
 * total debit eacm zone in function of number of eacm zone and debit from abacus
 */
export function totalDebitEacmZone(numberEacmZones: number, debit: number): number {
  return Number ((numberEacmZones * debit).toFixed(0));

}

/**
 * calculate leak rate from reference leak rate and chosen depressin
 */
export function leakRateCalculationFromDepression(refLeakRate: number, depression: number): number {
  return refLeakRate / Math.sqrt(20 / depression);
}

/**
 * Sum all value of number array
 */
export function reduceNumberArray(numbers: number[]): number {
  return numbers.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
}

/**
 * Calculate total air intakes
 */
export function totalSiteAirIntakesDebit(elementaryZones: ElementaryZone[]): number {
  const airDebits = elementaryZones.map((el) => el.depressionToMaintainValues.totalAirIntakesDebit);
  return reduceNumberArray(airDebits);
}

export function totalMinimalDepressionSiteAirIntakesDebit(
  elementaryZones: ElementaryZone[]
): number {
  const airDebits = elementaryZones.map((el) => el.minimalDepressionValues.totalAirIntakesDebit);
  
  return reduceNumberArray(airDebits);
}

export function totalEacmNumber(elementaryZones: ElementaryZone[], optional?:boolean): number {
  /*Evolution Majeure composition de EACM */
  if(optional){
    const eacmNb = elementaryZones.map((el) =>{
      if(el.optional) {
        return el.depressionToMaintainValues.eacmNumber;
      }
      else return 0
    });
    return reduceNumberArray(eacmNb);
  } /*Fin Evolution Majeure composition de EACM */
  else {
  const eacmNb = elementaryZones.map((el) =>{
    if(!el.optional) {
      return el.depressionToMaintainValues.eacmNumber;
    }
    else return 0
  });
  return reduceNumberArray(eacmNb);
}
}

export function calculateLeakDebit(leakRate: number, totalVolume: number): number {
  return Math.ceil(leakRate * totalVolume);
}

export function totalEarNumber(
  totalMaxExtractorCapacity: number,
  minimumAirDebitToExtract: number,
  earCapacity: number
): number {
  return Math.floor((totalMaxExtractorCapacity - minimumAirDebitToExtract) / earCapacity);
}

export function extractorNumber(
  minimumAirDebitToExtract: number,
  minimumExtraction: number,
  rescue: boolean
): number {
  const number = Math.ceil(minimumAirDebitToExtract / minimumExtraction);
  return rescue ? number + 1 : number;
}
export function minCapacityExtractor(
  number: number,
  minimumExtraction: number,
  isRescue: boolean
): number {
  const nb = isRescue ? number - 1 : number;
  return nb * minimumExtraction;
}

export function maxCapacityExtractor(number: number, maximumExtraction: number): number {
  return number * maximumExtraction;
}
/**
 * Calculate Renewal Rate per zone
 */
export function calculateRenewalRatePerZone(airIntakes: number, volume: number): number {
  return Number((airIntakes / volume).toFixed(1));
}
/**
 * calculate small extractor number without rescue
 * @param minimumAirDebitToExtract represents the minimum total air debit to extract
 * @param smallExtractor represents the extractor with de smallest capacities
 * @param nbAvailableExtractors represents the total number of small extractors available
 */
export function smallExtractorsNumberWithoutRescue(
  minimumAirDebitToExtract: number,
  minimumExtraction: number,
  nbAvailableExtractors: number
): number {
  return Math.min(Math.ceil(minimumAirDebitToExtract / minimumExtraction), nbAvailableExtractors);
}

/**
 * calculate big extractor number without rescue
 * @param minimumAirDebitToExtract represents the minimum total air debit to extract
 * @param smallExtractorNumber represents the total number of small extractors (the extractor with the smallest capacities)
 * @param bigExtractor represents the extractor with de greatest capacities
 */
export function bigExtractorsNumbersWithoutRescue(
  minimumAirDebitToExtract: number,
  smallExtractorNumber: number,
  smallExtractorMinimumExtraction: number,
  bigExtractorMinimumExtraction: number
): number {
  return Math.max(
    Math.ceil(
      (minimumAirDebitToExtract - smallExtractorNumber * smallExtractorMinimumExtraction) /
        bigExtractorMinimumExtraction
    ),
    0
  );
}

/**
 * calculate sas debit, eacm number and debit and ear number for each zone according to depressionvalue
 * @param elementaryZone current elementaryZone
 * @param depression depression chosen
 * @param bigExtractor represents the extractor with de greatest capacities
 */
export function calculateSasDepressionValues(
  elementaryZone: ElementaryZone,
  depression: number,
  settings: Settings,
  eacmEquipment: Equipment
): DepressionCalculatedValues {
  const depressionCalculatedValues: DepressionCalculatedValues = new DepressionCalculatedValues();

  //calculate personnelSas débit from abacus if zone as personnelEquipment
  depressionCalculatedValues.personnelSasDebit = elementaryZone.personnelEquipment?.abacus
    ? calculateDebitFromAbacus(elementaryZone.personnelEquipment.abacus, depression)
    : 0;

  //calculate wastesSas débit from abacus if zone as wasteEquipment
  depressionCalculatedValues.wastesSasDebit = elementaryZone.wasteEquipment?.abacus
    ? calculateDebitFromAbacus(elementaryZone.wasteEquipment.abacus, depression)
    : 0; 

  //calculate eacmequipment débit from abacus if zone as eacm
  depressionCalculatedValues.eacmDebit = eacmEquipment.abacus
    ? calculateDebitFromAbacus(eacmEquipment.abacus, depression)
    : 0;

  // then calculate total zone sas debit
  depressionCalculatedValues.totalSasDebit = calculateSasTotalZonesDebit(
    depressionCalculatedValues
  );
  
  

  // calculate minimal air zone debit of a zone
  depressionCalculatedValues.minimalAirZoneDebit = minimalAirZoneDebit(
    settings.renewalRate,
    elementaryZone.volume ? elementaryZone.volume : 0,
    depressionCalculatedValues.totalSasDebit
  );
  return depressionCalculatedValues;
}

/**
 * Calculate minimal and maximal depression Values. Calculate eacm numbers, but don't include ear values.
 */
export function calculateDepressionCalculatedValuesPerZone(
  zone: ElementaryZone,
  settings: Settings,
  eacmEquipment: Equipment
): ElementaryZone {
  zone.minimalDepressionValues = calculateSasDepressionValues(
    zone,
    settings.minimalDepressionValue,
    settings,
    eacmEquipment
  );
  zone.depressionToMaintainValues = calculateSasDepressionValues(
    zone,
    settings.depressionValuetoMaintain,
    settings,
    eacmEquipment
  );
  const eacmNumber = numberZoneEacm(
    zone.minimalDepressionValues.minimalAirZoneDebit,
    zone.minimalDepressionValues.eacmDebit
  );

  zone.minimalDepressionValues = calculateEacmInformations(
    eacmNumber,
    zone.minimalDepressionValues
  );
  zone.depressionToMaintainValues = calculateEacmInformations(
    eacmNumber,
    zone.depressionToMaintainValues
  );

  if (zone.volume) {
    zone.minimalDepressionValues.renewalRate = calculateRenewalRatePerZone(
      zone.minimalDepressionValues.totalAirIntakesDebit,
      zone.volume
    );
    zone.depressionToMaintainValues.renewalRate = calculateRenewalRatePerZone(
      zone.depressionToMaintainValues.totalAirIntakesDebit,
      zone.volume
    );
  }

  return zone;
}

export function calculateEacmInformations(
  eacmNumber: number,
  depressionCalculatedValues: DepressionCalculatedValues
): DepressionCalculatedValues {
  // set eacm number
  depressionCalculatedValues.eacmNumber = eacmNumber;

  // calculate total air debit of the eacm per zone
  depressionCalculatedValues.totalAirDebitEacm = totalDebitEacmZone(
    eacmNumber,
    depressionCalculatedValues.eacmDebit
  );

  // calculate total air intakes of a zone
  depressionCalculatedValues.totalAirIntakesDebit =
    depressionCalculatedValues.totalAirDebitEacm + depressionCalculatedValues.totalSasDebit;
    
    

  return depressionCalculatedValues;
}

export function totalMaxExtractorCapacity(
  smallExtractorNumber: number,
  smallExtractorMaxCapacity: number,
  bigExtractorNumber: number,
  bigExtractorMaxCapacity: number
): number {
  return (
    smallExtractorNumber * smallExtractorMaxCapacity + bigExtractorNumber * bigExtractorMaxCapacity
  );
}
/**
 * calculate minimal air debit to extract
 * total air intakes for chosen depression plus total leak debit
 */
export function calculateMinimalAirDebitToExtract(airIntakes: number, leakDebit: number): number {
  return airIntakes + leakDebit;
}
/**
 * calculate balance sheet from site elementaryzones data and chosen equipments
 * @param site represents the site with calculated value according to depressions, totalVolume and elementaryZones
 * @param settings global settings chosen by user
 * @param extractors all extractors data
 */
export function calculateBalanceSheet(
  site: SiteModel,
  settings: Settings,
  earEquipment: Equipment,
  extractors: {
    mandatoryExtractor: Extractor;
    optionalExtractor?: Extractor;
    mandatoryExtractorAvailable: number;
    optionalExtractorAvailable: number;
    isSecurityExtractorRequired: boolean;
  }
): BalanceSheetModel {
  const balanceSheet: BalanceSheetModel = new BalanceSheetModel();
  //sum up totalAirIntakes of each elementary zone
  balanceSheet.totalAirIntakesDebit = totalSiteAirIntakesDebit(site.elementaryZones);

  balanceSheet.depressionToMaintainRenewalRate =
    balanceSheet.totalAirIntakesDebit / site.totalVolume;
  balanceSheet.minimalDepressionRenewalRate =
    totalMinimalDepressionSiteAirIntakesDebit(site.elementaryZones) / site.totalVolume;
  //calculate leak rate of the depression to maintain
  balanceSheet.minimalDepressionLeakRate = leakRateCalculationFromDepression(
    settings.leakRate,
    settings.minimalDepressionValue
  );

  //calculate leak rate of the depression to maintain
  balanceSheet.depressionToMaintainLeakRate = leakRateCalculationFromDepression(
    settings.leakRate,
    settings.depressionValuetoMaintain
  );

  //calculate minimum air debit to extract according to total air intakes, leak rate and total site Volume
  balanceSheet.minimumAirDebitToExtract = calculateMinimalAirDebitToExtract(
    balanceSheet.totalAirIntakesDebit,
    calculateLeakDebit(balanceSheet.depressionToMaintainLeakRate, site.totalVolume)
  );

  /**Evolution Majeure composition de EACM
   * Scinder necessary en deux
   * si optinalEacm existe, mettre en place OptionalnecessaryEacmNumber et comme propriéte de balanceSheet en optinnel
   * sinon continuer avec necessaryEacmNumber
  */
  //sum up all necessary eacm number of each zone  
  balanceSheet.necessaryEacmNumber = totalEacmNumber(site.elementaryZones);

  /*Evolution Majeure composition de EACM */
  balanceSheet.optionalNecessaryEacmNumber = totalEacmNumber(site.elementaryZones, true);


  //Calcul WindSpeed
  balanceSheet.windSpeedRate = calculateWindSpeed(settings.depressionValuetoMaintain);

  // calculate necessary extractors
  if (!extractors.optionalExtractor) {
    balanceSheet.necessaryExtractorNumber = extractorNumber(
      balanceSheet.minimumAirDebitToExtract,
      extractors.mandatoryExtractor.minimumExtraction,
      extractors.isSecurityExtractorRequired
    );
    balanceSheet.bigExtractor.extractor = extractors.mandatoryExtractor;
    balanceSheet.totalMinExtractorCapacity = minCapacityExtractor(
      balanceSheet.necessaryExtractorNumber,
      extractors.mandatoryExtractor.minimumExtraction,
      extractors.isSecurityExtractorRequired
    );
    balanceSheet.totalMaxExtractorCapacity = maxCapacityExtractor(
      balanceSheet.necessaryExtractorNumber,
      extractors.mandatoryExtractor.maximumExtraction
    );

    balanceSheet.bigExtractor.number = balanceSheet.necessaryExtractorNumber;
  } else {
    let bigExtractorAvailable = 0;
    let smallExtractorAvailable = 0;
    // choose witch is the big extractor and witch is the small extractor
    if (
      Number(extractors.mandatoryExtractor.minimumExtraction) >=
      Number(extractors.optionalExtractor.minimumExtraction)
    ) {
      balanceSheet.bigExtractor.extractor = extractors.mandatoryExtractor;
      bigExtractorAvailable = extractors.mandatoryExtractorAvailable;
      balanceSheet.smallExtractor.extractor = extractors.optionalExtractor;
      smallExtractorAvailable = extractors.optionalExtractorAvailable;
    } else {
      balanceSheet.smallExtractor.extractor = extractors.mandatoryExtractor;
      smallExtractorAvailable = extractors.mandatoryExtractorAvailable;
      balanceSheet.bigExtractor.extractor = extractors.optionalExtractor;
      bigExtractorAvailable = extractors.optionalExtractorAvailable;
    }
    // get number of small extractors
    balanceSheet.smallExtractor.number = smallExtractorsNumberWithoutRescue(
      balanceSheet.minimumAirDebitToExtract,
      balanceSheet.smallExtractor.extractor.minimumExtraction,
      Number(smallExtractorAvailable)
    );

    // get number of big extractors
    balanceSheet.bigExtractor.number = bigExtractorsNumbersWithoutRescue(
      balanceSheet.minimumAirDebitToExtract,
      balanceSheet.smallExtractor.number,
      balanceSheet.smallExtractor.extractor.minimumExtraction,
      balanceSheet.bigExtractor.extractor.minimumExtraction
    );

    // get number of min extractor capacity
    balanceSheet.totalMinExtractorCapacity =
      balanceSheet.smallExtractor.number * balanceSheet.smallExtractor.extractor.minimumExtraction +
      balanceSheet.bigExtractor.number * balanceSheet.bigExtractor.extractor.minimumExtraction;

    // if isSecurityExtractorRequired add a big extractor
    balanceSheet.bigExtractor.number = extractors.isSecurityExtractorRequired
      ? balanceSheet.bigExtractor.number + 1
      : balanceSheet.bigExtractor.number;

    // get total max extractor capacity
    balanceSheet.totalMaxExtractorCapacity = totalMaxExtractorCapacity(
      balanceSheet.smallExtractor.number,
      balanceSheet.smallExtractor.extractor.maximumExtraction,
      balanceSheet.bigExtractor.number,
      balanceSheet.bigExtractor.extractor.maximumExtraction
    );
  }

  if (earEquipment) {
    //calculate earequipment débit from abacus
    balanceSheet.earCapacity = earEquipment.abacus
      ? calculateDebitFromAbacus(earEquipment.abacus, settings.depressionValuetoMaintain)
      : 0;

    // calculate necessary earNumber
    balanceSheet.necessaryEarNumber = totalEarNumber(
      balanceSheet.totalMaxExtractorCapacity,
      balanceSheet.minimumAirDebitToExtract,
      balanceSheet.earCapacity
    );

    // get elementary zones having personnel decontamination
    const elementaryZones = getPersonnelDecontaminationZones(site);
    let earNumber = balanceSheet.necessaryEarNumber;
    elementaryZones.forEach((zone, index) => {
      if (elementaryZones.length == 1) {
        zone.minimalDepressionValues.earNumber = earNumber;
        zone.depressionToMaintainValues.earNumber = earNumber;
      } else {
        const vol = zone.volume ? zone.volume : 1;
        const numberEar = Math.ceil(balanceSheet.necessaryEarNumber * (vol / site.totalVolume));
        zone.minimalDepressionValues.earNumber = index == 0 ? numberEar : earNumber;
        zone.depressionToMaintainValues.earNumber = zone.minimalDepressionValues.earNumber;
        earNumber -= numberEar;
      }
      zone = calculateEarZone(zone, settings, earEquipment);
    });
  }



  return balanceSheet;
}

function getPersonnelDecontaminationZones(site: SiteModel): ElementaryZone[] {
  return site.elementaryZones.filter((el) => el.personnelDecontamination);
}

function calculateEarForDepression(
  depressionValue: DepressionCalculatedValues,
  depression: number,
  earEquipment: Equipment
) {
  const earCapacity = earEquipment.abacus
    ? calculateDebitFromAbacus(earEquipment.abacus, depression)
    : 0;
  depressionValue.earCapacity = Number((depressionValue.earNumber * earCapacity).toFixed(0));
  return depressionValue;
}

function calculateEarZone(
  elementaryZone: ElementaryZone,
  settings: Settings,
  earEquipment: Equipment
): ElementaryZone {
  elementaryZone.minimalDepressionValues = calculateEarForDepression(
    elementaryZone.minimalDepressionValues,
    settings.minimalDepressionValue,
    earEquipment
  );
  elementaryZone.depressionToMaintainValues = calculateEarForDepression(
    elementaryZone.depressionToMaintainValues,
    settings.depressionValuetoMaintain,
    earEquipment
  );

  return elementaryZone;
}

//Calcul d'avertissement de depression renseignée
export function calculateWindSpeed( depression: number): number {
  return Number((3.6 *  Math.sqrt((5 * depression) /1.2)).toFixed(0));
}